import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Cell, Tooltip, Pie, PieChart } from "recharts";
import { COLORS, CustomLegend, legendColors, } from "./utils/ChartUtils";

const COUNTS = [
  "0-0", "0-1", "0-2",
  "1-0", "1-1", "1-2",
  "2-0", "2-1", "2-2",
  "3-0", "3-1", "3-2",
];

function buildCountData(pitchData: any[]): Record<string, { pitch_type: string; pitch_name: string; usage_pct: number; count: number }[]> {
  const result: Record<string, { pitch_type: string; pitch_name: string; usage_pct: number; count: number }[]> = {};

  for (const cnt of COUNTS) {
    const [b, s] = cnt.split("-").map(Number);
    const rows = pitchData.filter(
      (p) => Number(p.balls) === b && Number(p.strikes) === s && p.pitch_type
    );
    const totals: Record<string, { pitch_type: string; pitch_name: string; count: number }> = {};
    for (const row of rows) {
      const pt = row.pitch_type as string;
      if (!totals[pt]) {
        totals[pt] = { pitch_type: pt, pitch_name: row.pitch_name ?? pt, count: 0 };
      }
      totals[pt].count += 1;
    }
    const total = rows.length;
    result[cnt] = (Object.keys(totals) as string[])
      .map((k) => totals[k])
      .sort((a: { count: number }, b: { count: number }) => b.count - a.count)
      .map((entry: { pitch_type: string; pitch_name: string; count: number }) => ({
        ...entry,
        usage_pct: total > 0 ? (entry.count / total) * 100 : 0,
      }));
  }

  return result;
}

const renderTooltip = (props: any) => {
  const { active, payload } = props;
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div style={{ backgroundColor: "#fff", border: "1px solid #999", padding: 8 }}>
        <strong>{data.payload.pitch_name}</strong>
        <div>{data.payload.usage_pct.toFixed(1)}%</div>
        <div style={{ color: "#666", fontSize: "0.75rem" }}>{data.payload.count} pitches</div>
      </div>
    );
  }
  return null;
};

export default function CountChart({ pitchData, isDesktop, playerDataProp, pitches }: { pitchData: any[]; isDesktop?: boolean; playerDataProp?: any; pitches?: string[] }) {
  const countData = useMemo(() => buildCountData(pitchData), [pitchData]);

  if (!pitchData || pitchData.length === 0) return null;

  const pieSize = isDesktop ? 78 : 66;
  const outerRadius = isDesktop ? 30 : 26;

  const teamColors: { [id: string]: string[] } = {
    TBR: ["#02285C", "#90BDE7", "#FFFFFF"], BAL: ["#F25D23", "#000000", "#FFFFFF"],
    NYY: ["#132448", "#FFFFFF", "#FFFFFF"], BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
    TOR: ["#134A8E", "#E8291C", "#FFFFFF"], CLE: ["#E31937", "#002B5C", "#FFFFFF"],
    KCR: ["#004687", "#FFFFFF", "#FFFFFF"], DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
    CHW: ["#000000", "#BFBFBF", "#FFFFFF"], MIN: ["#091F40", "#E31E34", "#FFFFFF"],
    HOU: ["#002D62", "#F4911E", "#FFFFFF"], SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
    TEX: ["#C0111F", "#003278", "#FFFFFF"], LAA: ["#BA0021", "#003263", "#C4CED4"],
    ATH: ["#00342C", "#FFB400", "#FFFFFF"], WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
    NYM: ["#F75500", "#002C6E", "#FFFFFF"], MIA: ["#019CD6", "#E51829", "#FFFFFF"],
    PHI: ["#C21D39", "#01396F", "#FFFFFF"], ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
    CIN: ["#C6011F", "#000000", "#FFFFFF"], PIT: ["#F7BF02", "#000000", "#FFFFFF"],
    CHC: ["#123581", "#E62F2E", "#FFFFFF"], MIL: ["#18294C", "#FEC430", "#FFFFFF"],
    STL: ["#BE0A15", "#FDDB00", "#FFFFFF"], LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
    ARI: ["#C71128", "#00C2CE", "#FFFFFF"], SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
    SFG: ["#231F20", "#F4793E", "#E8D8C4"], COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
    FA:  ["#BFBFBF", "#FFFFFF", "#FFFFFF"], "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
  };

  const org: string = playerDataProp?.MLB_AbbName ?? "FA";
  const colors = teamColors[org] ?? teamColors["FA"];

  return (
    <Grid item maxWidth="90vw">
      <Box
        sx={{
          maxWidth: "90vw",
          width: "500px",
          border: "#293241 solid 2px",
          backgroundColor: "#FFFFFF",
          height: "100%",
          boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
        }}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {/* Header — matches MovementProfile / PitchSplits style */}
        <div style={{ width: "100%", paddingTop: "10px", paddingBottom: "10px", backgroundColor: colors[0] }}>
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Typography variant="h5">
              <span style={{ color: colors[1], fontWeight: "bold", fontSize: isDesktop ? "25pt" : "18pt", textAlign: "center" }}>
                Pitch Mix by Count
              </span>
            </Typography>
          </Grid>
        </div>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 0.5,
          px: 1,
          py: 0.5,
        }}
      >
        {COUNTS.map((cnt) => {
          const data = countData[cnt] ?? [];
          const total = data.reduce((s, d) => s + d.count, 0);
          return (
            <Box
              key={cnt}
              sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 0.25 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {cnt}
              </Typography>
              <Typography variant="caption" sx={{ color: "#888", fontSize: "0.6rem", lineHeight: 1.1 }}>
                {total}
              </Typography>
              {data.length > 0 ? (
                <PieChart width={pieSize} height={pieSize}>
                  <Pie
                    data={data}
                    dataKey="usage_pct"
                    nameKey="pitch_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={outerRadius}
                    isAnimationActive={false}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[entry.pitch_type] ?? "#999999"}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={renderTooltip} />
                </PieChart>
              ) : (
                <Box
                  sx={{
                    width: pieSize,
                    height: pieSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ccc",
                    fontSize: "0.7rem",
                  }}
                >
                  —
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Legend — matches MovementProfile / PitchHeatmap style */}
        <CustomLegend colors={legendColors.filter((c) => (pitches ?? []).includes(c.key))} />
      </Box>
    </Grid>
  );
}
